import { GetNotePreviewRespFields } from "../packages/api/shared/constants"
import { CHAINS } from "./collectibles"
import { PostMetricsResp } from "./metrics"
import { BaseUserPublic, emptyUser, User } from "./users"

export type SOCIAL_MEDIA_FIELD_NAMES = keyof SocialMediaLinks

export interface SocialMediaLinks {
  farcaster?: string
  twitter?: string
  github?: string
  instagram?: string
  facebook?: string
}

// Used to mint a Paragraph token. This is entirely on-chain.
export interface OnChainCryptoData {
  name: string
  // Optional because when minting a Paragraph token, the user can choose not to specify the total quantity so that there's unlimited mints available.
  totalQuantity?: number
  maticPrice: number
}

export interface CryptoToken {
  // Also known as a tier
  // These exist both in the db AND on the blockchain
  id: string
  //priceWei: number // The price is also enforced in the smart contract.
  //priceWei: BigNumber
  //totalAmount: BigNumber

  // These exist ONLY on the blockchain.
  //remainingAmount: number

  // These exist ONLY in the db.
  name: string
}

type WalletString = `0x${string}`

export interface CryptoData {
  wallet_address?: WalletString
  tokens?: Array<CryptoToken>
}

export type Link = {
  friendly_name: string
  url: string
}

export type PostListType = "feed" | "grid" | "full-post"

/**
 * - latest: Latest post (default)
 * - popular: Your most popular post
 * - string: A noteID of the specific post
 * - disabled: No featured post. This is the default.
 */
export type FeaturedPostType = "latest" | "popular" | string | "disabled"

/**
 * All possible theme colors for a user's publication.
 *
 * Default is slate-950
 */
export const THEME_COLORS = {
  /**
   * Slate 950
   */
  default: "Default",
  "orange-500": "Orange",
  "rose-600": "Rose",
  "pink-500": "Pink",
  "fuchsia-600": "Fuchsia",
  "purple-600": "Purple",
  "violet-600": "Violet",
  "indigo-600": "Indigo",
  "blue-600": "Blue",
  "sky-600": "Sky Blue",
  "cyan-600": "Cyan",
  "teal-600": "Teal",
  "emerald-600": "Emerald",
  "green-600": "Green",
  "lime-600": "Lime",
  "yellow-700": "Gold",
  "amber-600": "Amber",
  "orange-600": "Dark Orange",
  "stone-800": "Stone",
  "rose-900": "Dark Rose",
  "indigo-950": "Deep Indigo",
  "teal-950": "Deep Teal",
  "lime-950": "Deep Lime",
  "orange-950": "Deep Orange",
} as const

export type ThemeColor = keyof typeof THEME_COLORS
export type ThemeFont = "default" | "serif" | "mono"

export type BlogTheme = {
  /**
   * Post list type
   * If undefined, the default is "feed".
   *
   * feed: Single column feed of post cards
   * grid: 3-column grid of small post cards
   * full-post: Single column feed of full posts
   */
  postListType?: PostListType
  /**
   * If featured post is enabled and what to show.
   * If undefined, then it defaults to "disabled" via helper function.
   *
   * latest: Latest post
   * popular: Your most popular post
   * [id]: A noteID of the specific post
   */
  featuredPost?: FeaturedPostType

  /**
   * Whether to show the "most popular posts" widget.
   *
   * If it's true, it's shown. If it's undefined it defaults to false. The user can also set it to false.
   * When it's false is it hidden.
   */
  mostPopular?: boolean

  /**
   * The color of the publication.
   */
  color?: ThemeColor

  /**
   * The header font of the publication.
   */
  headerFont?: ThemeFont

  /**
   * The body font of the publication.
   */
  bodyFont?: ThemeFont

  /**
   * If true, we hide a publication's stats such as subscriber count, number of posts, collects, etc.
   * which some writers feel is sensitive. This is undefined by default and by default we show them
   * unless the user flips this toggle to true.
   */
  hideStats?: boolean
}

export type allPostsTheme = {
  showCoverImg?: boolean
  coverImgPosition?: "above" | "left" | "right"

  //coverImgMaxHeight?: number

  showTitle?: boolean

  showTags?: boolean

  /**
   * The default is md.
   */
  style?: "xl" | "lg" | "md" | "sm" | "xs"

  /**
   * The default is 1.
   */
  gridCols?: number

  showDescription?: boolean

  showReadMore?: boolean
  showDate?: boolean

  /**
   * The default is 10.
   */
  postsPerPage?: number

  /**
   * Whether we display the full post on the homepage, or
   * just a snippet.
   *
   * Default is false.
   */
  fullPost?: boolean
}

export type ReputationLevel =
  | "HIGH"
  | "MEDIUM"
  | "MEDIUM_LOW"
  | "LOW"
  | "VERY_LOW"

export interface Blog {
  id: string
  name: string
  url: string

  // This is the name that appears on the "From" field
  // of the email that is sent to the user.
  sender_email_name?: string

  // Has the user purchased their custom domain?
  custom_domain_purchased?: boolean

  // Only if the user has purchased custom domain - what is it?
  custom_domain?: string
  lowercase_url: string

  summary?: string // This is one sentence

  /**
   * @deprecated: Use summary instead.
   */
  about?: string

  createdAt: number
  updatedAt?: number
  user?: User
  social?: SocialMediaLinks
  // @DEPRECATED. Now stored on the `User` type. Can comment out after migration done.
  author_name?: string
  logo_url?: string

  /**
   * Whether comments are disabled.
   *
   * This can be for ALL comments, or just for on-platform comments.
   *
   * true: ALL comments are disabled
   *   on-platform: On-platform comments are disabled, BUT Farcaster comments are still enabled.
   *   false: Comments are enabled.
   */
  disable_comments?: boolean | "on-platform"

  disable_highlights?: boolean

  /* Whether the table of contents is enabled. */
  enable_table_of_contents?: boolean

  /**
   * Whether the subscribe popup is enabled.
   * This is the initial popup that gets displayed for new users,
   * whenever they land on this publication.
   *
   * This only happens once per user per blog (we set a cookie so it doesn't trigger again).
   * The user must also be coming from an external link.
   */
  enable_subscribe_popup?: boolean

  /**
   * Whether the subscribe popup *on scroll* is enabled.
   * This is the initial popup that gets displayed whenever new users
   * scroll down on a post.
   *
   *
   * This only happens once per user per blog (we set a cookie so it doesn't trigger again).
   * The user must also be coming from an external link.
   */
  enable_subscribe_scroll?: boolean

  /** The Stripe Connect Account ID for the blog */
  stripe_account_id?: string
  /** This is set when the Stripe account has been fully configured */
  charges_enabled?: boolean

  /**
   * This is set when the Loop account is connected.
   * It is needed because you can't create multiple Loop accounts for the same
   * email so we never clear their Loop info and instead use this flag.
   */
  loop_enabled?: boolean
  /** The Loop Child Entity ID for the blog */
  loop_entity_id?: string
  /** The wallet for Loop payouts */
  loop_wallet_address?: string
  /** The names of the networks accepted for Loop payment collection */
  enabled_loop_networks?: LoopNetworkName[]
  /** The names of the tokens accepted for Loop payment collection */
  enabled_loop_tokens?: LoopTokenName[]

  memberships?: Membership[]

  crypto?: CryptoData

  // Color customization.
  // If these are null or undefined, fall back to regular color.
  primary_color?: string | null

  // The secondary color is the background color.
  secondary_color?: string | null
  font_color?: string | null

  // The string name of the font.
  font?: string | null

  // The string name of the font used for headers.
  header_font?: string | null

  /**
   * The custom CSS for the blog.
   */
  custom_css?: string

  // Custom analytics code
  google_analytics_id?: string

  /**
   * Customize posts display in the 'all-posts' page.
   * @deprecated: Use blogTheme instead
   */
  allPostsTheme?: allPostsTheme

  /**
   * The new theme settings of the blog.
   */
  theme?: BlogTheme

  /**
   * Whether the theme settings have been auto-migrated.
   * This is set to true when the theme settings are migrated from the old format to the new format using a script,
   * rather than manually set by the user.
   */
  isThemeSettingsAutoMigrated?: boolean

  // The links that get displayed on the user's blog's header.
  links?: Array<Link>

  /**
   * The user's blog's email settings.
   * If these are undefined, they default to TRUE.
   */
  email_notifications?: {
    new_comment?: boolean
    new_subscriber?: boolean // Free subscriber only
    new_paid_subscriber?: boolean // Paid subscriber only
    new_content_collected?: boolean
  }

  /**
   * All new user's blogs are MEDIUM_LOW by default.
   *
   * VERY_LOW: User can't publish nor send newsletters, and are hidden from sitemap / discover pages.
   * LOW: User can't send newsletters and are hidden from sitemap / discover pages.
   * MEDIUM_LOW: User can't send newsletters to a large number of subscribers.
   * MEDIUM: User is manually verified and can do anything.
   * HIGH: User can do anything, and additionally their posts are displayed on TRENDING page.
   */
  reputation?: ReputationLevel

  // If set to true, direct the user to the setup page.
  needToSetup?: boolean

  /**
   * The cost of all collectibles and highlights for the user.
   *
   * This is in ETH, NOT wei.
   */
  highlightsCost?: number
  highlightsSupply?: string
  highlightsChain?: CHAINS

  /**
   * This wallet address receives all funds
   * for all collectibles that this blog creates.
   * This can be overridden by the note-level wallet address.
   * If this is undefined, the blog author's wallet address is used.
   *
   * NOTE: If a user changes their collectibleWalletAddress on their blog after having already earned revenue,
   * it would instantly make them lose all historical revenue metrics prior to that change when looking at their
   * post revenue. If this starts happening a lot, we might need to create a new historicalCollectibleWalletAddress
   * field on the post to store the original wallet address(es) and match on those too, if present.
   */
  collectibleWalletAddress?: string

  // This is the date that the latest post this blog owns was modified.
  // This is used to include in the sitemap.
  latestPostModifiedTs?: number

  /**
   * The setting that controls how this blog's posts are ordered in the dashboard.
   * Undefined if they haven't set this (default is to order by "updatedAt").
   * If the blog dashboard is being viewed by a team member, then there's also a setting
   * on the TeamMember record that can overwrite this one, if set.
   */
  orderNotesInDashboardBy?: NoteOrderByPreference

  /**
   * IDs of recommended publications
   */
  recommendations?: RecommendationItem[]

  /**
   * An ordered array of note IDs that are pinned to the top of the blog's homepage.
   */
  pinnedPostIds?: string[]

  /**
   * This is the user ID that owns the blog.
   *
   * This is functionally equivalent to the blogRef.parent.parent.id.
   *
   * We store this here for redundancy, to make certain queries easier.
   */
  userId?: string

  /**
   * Whether this blog has any gated posts.
   */
  hasGates?: boolean

  /**
   * The number of gated posts for this blog. We display this to them in the UI.
   */
  gatedPostCount?: number

  /**
   * Whether all of this blog's gated posts are archived.
   */
  gatedPostsAllArchived?: boolean

  /**
   * Whether this blog has taken any action on their gated posts (since we're deprecating them).
   */
  hasActionTakenOnGates?: boolean

  /**
   * Whether this blog has been forced to deprecate gated posts via a migration.
   */
  hasForcedGateDeprecation?: boolean
}

export type AiGeneratedPostValues = {
  coverImage?: boolean
  title?: boolean
  subtitle?: boolean
  contents?: boolean
}

/**
 * Includes the number of subscribers for the blog.
 * Used in the admin dashboard.
 */
export type BlogWithAdminInfo = Blog & {
  subscribers: number
}

/**
 * How to order notes in the dashboard. Default is "updatedAt" when nothing is set in Firestore.
 */
export type NoteOrderByPreference =
  | "publishedAtDesc"
  | "publishedAtAsc"
  | "updatedAtDesc"
  | "updatedAtAsc"
  | "createdAtDesc"
  | "createdAtAsc"

export type ImageWithPlaceholder = {
  base64: string
  img: {
    width: number
    height: number
    src: string
  }
}

/**
 * Email params to use when getting a list of blog emails.
 */
export interface GetBlogEmailParams {
  blogId: string
}

/**
 * Email params to use when creating a new blog email.
 */
export interface PostBlogEmailParams {
  blogId: string
}

/**
 * Response when creating a new blog email.
 */
export interface PostBlogEmailRes {
  /**
   * The ID of the newly created blog email.
   */
  id: string
}

/**
 * Email params to use when updating an existing blog email.
 */
export interface PutBlogEmailParams {
  blogId: string
  emailId: string
}

export type UserAndSubscription = {
  user: User
  subscription: Subscription
  /**
   * Enriched array of opt-outs (either communities or the "nonCommunityEmails" string).
   */
  optOuts?: SubscriptionOptOutOrIn[]
  /**
   * Enriched array of opt-ins (either communities or the "nonCommunityEmails" string).
   */
  optInts?: SubscriptionOptOutOrIn[]
}

export type UserSubscriptionAndMembership = {
  user: User
  subscription: Subscription
  membership: Membership
  /**
   * Enriched array of opt-outs (either communities or the "nonCommunityEmails" string).
   */
  optOuts?: SubscriptionOptOutOrIn[]
  /**
   * Enriched array of opt-ins (either communities or the "nonCommunityEmails" string).
   */
  optInts?: SubscriptionOptOutOrIn[]
}

export type SubscriptionAndMembership = {
  subscription: Subscription
  membership: Membership
}

// Contains data about the page of records retrieved as well as about the table as a whole.
export type QueryData = {
  pageSize: number
  pageOffset: number
  totalRecords: number
}

export type UserAndSubscriptionPage = {
  records: Array<UserAndSubscription>
  query: QueryData
}

export type UserSubscriptionAndMembershipPage = {
  records: Array<UserSubscriptionAndMembership>
  query: QueryData
}

export type BlogAndSubscription = {
  blog: Blog
  subscription: Subscription
  /**
   * All of this blog's communities, useful for managing subscription opt-out preferences by a reader.
   */
  blogCommunities: Array<CommunityEntry>
}

export type BlogSubscriptionAndMembership = {
  blog: Blog
  subscription: Subscription
  membership: Membership
  /**
   * All of this blog's communities, useful for managing subscription opt-out preferences by a reader.
   */
  blogCommunities: Array<CommunityEntry>
}

export type SubscriberCountResp = {
  active: number
  unsubscribed: number
  total: number
  activeAndOptedIntoGeneral: number
}

export type SubscriptionType = "MANUALLY_ADDED" | "IMPORTED" | "ORGANIC"
export type SubscriptionStatus = "ACTIVE" | "UNSUBSCRIBED" | "PAST_DUE"

// This is a fake community that's a placeholder for all non-community related emails
// (e.g. posts that have no community specified).
export type SubscriptionOptOut_NonCommunityEmails_FakeCommunity = Omit<
  CommunityEntry,
  "blogId"
>

/** Includes both the real community record stored in Firestore,
 * and the fake community placeholder for "General Emails" that
 * isn't really a community. This allows us to store the general
 * emails opt-out in the `optOuts` array for a `Subscription`.
 * Its ID is stored as `nonCommunityEmails` in the array. */
export type SubscriptionOptOutOrIn =
  | CommunityEntry
  | SubscriptionOptOut_NonCommunityEmails_FakeCommunity

export interface UserBlogs {
  blogs: Array<Blog>
}

export const EmptyBlog: Blog = {
  id: "",
  name: "",
  about: "",
  url: "",
  lowercase_url: "",
  author_name: "",
  user: emptyUser,
  createdAt: Date.now(),
}

export type Note = {
  id: string
  json?: string
  canonicalUrl?: string
  // TODO: Remove after implementing generation on API server
  staticHtml?: string
  text?: string
  title?: string
  subtitle?: string
  categories?: Array<string>
  createdAt?: number
  updatedAt?: number

  /**
   * In a unique circumstance, where we're returning the notes to the frontend in the writer's dashboard and the writer is explicitly filtering by
   * 'all published notes', we need to fetch the _latest draft_ of a published note _but_ set published: true, so the writer can properly edit the
   * latest draft of the published post.
   *
   * This is done at the API level.
   */
  published?: boolean

  publishedAt?: number

  /**
   * If this post is a draft, this is when the draft was created.
   *
   * As of 12/13/2023, this is only used for debugging (eg data recovery)
   * purposes.
   */
  draftCreatedAt?: number

  /**
   * This is true if a particular version of a post is the latest draft of a group of posts.
   * For instance, if:
   * 1. This is the one and only post and it's not yet been published.
   * 2. This is the latest post in a group of posts and is a more recent draft than the currently published post.
   * 3. This is the latest post in a group of posts, but the main post has actually since been unpublished.
   *
   * In the following scenarios, this would be false:
   * 4. If this is the currently published post.
   * 5. If this is an old draft (and there is a newer draft, or if the published post in the group is newer).
   *
   * You can use this to query for all drafts for a blog with limits and offsets, by using for instance:
   * .where("archived", "==", false)
   * .where("isLatestDraft", "==", true)
   * .orderBy("updatedAt", "desc")
   * .offset(offset)
   * .limit(limit)
   *
   * And this would always return a list of the latest drafts for each post group.
   * You would use this, for instance, to populate the drafts post list in the writer's dashboard.
   *
   * Scenario 1:
   * There's just one post in the group.
   *
   * [{
   *    id: 1,
   *    draftOf: null,
   *    published: false,
   *    isLatestDraft: true,
   * }]
   *
   * Scenario 2:
   * In this example, the post with id 2 is the main post, the post with id 1 is an earlier draft (that had been previously published), and the post with id 3 is
   * the latest draft.
   *
   * [{
   *    id: 1,
   *    draftOf: 2,
   *    published: false,
   *    isLatestDraft: false,
   * },
   * {
   *    id: 2,
   *    draftOf: null,
   *    published: true,
   *    isLatestDraft: false,
   * },
   * {
   *    id: 3,
   *    draftOf: 2,
   *    published: false,
   *    isLatestDraft: true,
   * }]
   *
   * Scenario 3:
   * In this example, the post with id 2 was the main post, the post with id 1 is an earlier draft (that had been previously published), and the post with id 3 is
   * the latest draft. However, the user has since hit "Unpublish" on te post with id 2, so none of them are published anymore, but we still have a latest draft.
   *
   * [{
   *    id: 1,
   *    draftOf: 2,
   *    published: false,
   *    isLatestDraft: false,
   * },
   * {
   *    id: 2,
   *    draftOf: null,
   *    published: false,
   *    isLatestDraft: false,
   * },
   * {
   *    id: 3,
   *    draftOf: 2,
   *    published: false,
   *    isLatestDraft: true,
   * }]
   *
   * Scenarios 4 & 5:
   * In this example the latest post is actually the published post, and the draft is an old draft, in which case there is no isLatestDraft in the group.
   *
   * [{
   *    id: 1,
   *    draftOf: 2,
   *    published: false,
   *    isLatestDraft: false,
   * },
   * {
   *    id: 2,
   *    draftOf: null,
   *    published: true,
   *    isLatestDraft: false,
   * }]
   */
  isLatestDraft: boolean

  /**
   * This field is used to quickly figure out if there's a newer draft of the currently published post. This should only ever be true for published posts (`published` == true).
   * This skips needing to do a separate DB query to see if there's another post with `draftOf` pointing to this published post, and if it has a more recent `updatedAt` timestamp.
   *
   * You could run a query for all published posts by doing:
   *
   * .where("archived", "==", false)
   * .where("published", "==", true)
   * .orderBy("updatedAt", "desc")
   *
   * And then paginate with limit and offset as needed.
   *
   * You would then run a for loop and for each published post, if it has `latestDraftId` set, you can query that draft id and show that to the writer (together with adding a "Draft"
   * badge next to the "Published" badge for those posts since they have a more recent draft).
   *
   * This would be used to populate the writer's published post list on their dashboard.
   * Note that in this situation we need to fetch the _latest draft_ of a published note _but_ set published: true, so the writer can properly edit the latest draft of the published post.
   *
   * If this is ever not populated, it should be set to null to make it easier to query (since Firestore can't query undefined fields).
   */
  latestDraftId: string | null

  /* If this is true, the pulishedAt field was set manually,
   * so we never should update it.
   */
  manualPublishedAt?: boolean

  /**
   * Number of times that this post has been published.
   */
  numTimesPublished?: number

  archived?: boolean

  /**
   * As soon as a post is archived, the slug becomes null.
   * Please refer to the conversation for additional context:
   * https://github.com/paragraph-xyz/monorepo/pull/2712#discussion_r1725990755
   */
  slug?: string | null

  /**
   * This may be auto-generated, but if it's manually set then isPostPreviewManual will be set to true,
   * at which point we won't override it when auto-generating a post preview.
   */
  post_preview?: string
  /**
   * Records whether a post_preview was set manually, and if so, does not override it when
   * generating a post preview during auto-save.
   */
  isPostPreviewManual?: boolean
  about_me?: boolean // DEPRECATED: Use Links in the Blog object to set up any post as any link.

  /** This is the cover image of the post. It's either the hero image (which was manually uploaded by the user;
   * meaning isHero would be true), or it's the first image in the post.
   */
  cover_img?:
    | (ImageWithPlaceholder & {
        // If this is
        isHero: boolean
      })
    | null

  /**
   * @deprecated Use cover_img instead.
   */
  cover_img_url?: string

  // DEPRECATED: Replaced by communityId below.
  accessRestriction?: {
    paid?: boolean // Requires Stripe paid subscription
    token?: boolean // Requires reader to be a token-holder
    all?: boolean
  } | null

  // Is this posted in a specific community?
  communityId?: string

  // Should this blogpost be sent as a newsletter?
  sendNewsletter?: boolean

  // Should this blogpost be sent via XMTP?
  sendXMTP?: boolean

  /**
   * Should this post NOT be published online?
   * Note that the vast majority of posts WILL be published online,
   * so this will be undefined. Only a few posts will have this set to true.
   */
  dontPublishOnline?: boolean

  // Should this post be stored on Arweave?
  storeOnArweave?: boolean

  // When it's stored, what's the arweave ID?
  arweaveId?: string

  /**
   * Points to a noteId of which this is a draft. If nothing here, that means it's not a draft but a published (or as yet unpublished) post.
   *
   * In other words, if this is present, then this is a draft of another post. As soon as this gets published, draftOf will become null.
   */
  draftOf?: string | null

  /**
   * Set by the API as true for any truncated 'post-level' gates, and false for any 'after' gates or for full posts.
   *
   * Not stored in the db, but set by the API when returning a post.
   */
  isPostGateTruncated?: boolean

  /**
   * Set by the API as true for any truncated 'after' gates, and false for any 'post' gates or for full posts.
   *
   * Not stored in the db, but set by the API when returning a post.
   */
  isAfterGateTruncated?: boolean

  /* Is this post generated by a user (eg not the Paragraph welcome post)?
     If so, this will be set to true.
     We need this set to true so we can query on, for example, all user-generated posts
     for the Discover page. (Firestore limitations means it's harder to combine multiple
     inequality filters, but combining multiple equality filters is OK.)
     We also set this to 'false' whenever we disable a user's account.
  */
  ugc?: boolean

  /**
   * Whether collectibles are disabled on this post.
   * If this is true, we don't show the collectibles UI on the post.
   */
  collectiblesDisabled?: boolean
  /**
   * The collectible cost set on the note. This overrides the blog-level
   * cost.
   * Note that this is for ALL collectibles, not just highlights; it's named this
   * for historical reasons.
   *
   * This is in ETH, NOT wei.
   */
  highlightsCost?: number
  /**
   * The collectible supply set on the note. This overrides the blog-level
   * supply.
   * Note that this is for ALL collectibles, not just highlights; it's named this
   * for historical reasons.
   */
  highlightsSupply?: string
  /**
   * The collectible chain set on the note. This overrides the blog-level
   * chain.
   * Note that this is for ALL collectibles, not just highlights; it's named this
   * for historical reasons.
   */
  highlightsChain?: CHAINS

  /**
   * Whether this collectible has HTML enabled.
   *
   * If HTML is enabled we return `animation_url` pointing to the raw
   * HTML of the collectible, which certain marketplaces can render.
   *
   * If it's disabled, we always display the image.
   */
  collectibleHtml?: boolean
  /**
   * The URL of the collectible image. This is manually uploaded by the user.
   *
   * If this isn't present, we fallback to defaultCollectibleImgUrl,
   * which is the autogenerated "card" image.
   */
  collectibleImgUrl?: string

  /**
   * The URL of the collectible image that we autogenerate, based on the post title, cover image,
   * etc.
   *
   * Note that if collectibleImgUrl is present, the user manually uploaded their own image,
   * so we use that instead.
   */
  defaultCollectibleImgUrl?: string

  /**
   * This is the wallet address that receives
   * funds from the collectible.
   *
   * NOTE: If a user changes their collectibleWalletAddress on their note after having already earned revenue,
   * it would instantly make them lose all historical revenue metrics prior to that change when looking at their
   * post revenue. If this starts happening a lot, we might need to create a new historicalCollectibleWalletAddress
   * field on the post to store the original wallet address(es) and match on those too, if present.
   */
  collectibleWalletAddress?: string

  // Contains useful newsletter and post metrics.
  metrics?: PostMetricsResp

  /**
   * List of one or more userIds that have been explicitly set as the 'author'
   * of this post.
   *
   * By default this is the userId of the user that created the note.
   * More can be manually added.
   *
   * Note that this does NOT imply that the user has contributed to the post;
   * just that they are displayed as an author.
   */
  authors: string[]

  /**
   * If the post is on the 'trending' page (eg, the user that created this is high quality.
   */
  trending?: boolean

  /**
   * List of one or more user IDs that have contributed to this post by editing it.
   *
   * Note that this is NOT the same as authors. A post can have 10 people editing it but only
   * set one of them as the author. In that case, this would have all 10 userIDs, but the authors
   * field above one only have one.
   */
  contributors?: string[]

  /**
   * Whether this post was imported from a competing platform,
   * via our post import tool.
   */
  isImported?: boolean

  /**
   * If a post is imported from a competing platform, we store the original path of the
   * post here, with NO starting or ending slashes. This is used to preserve SEO for the original post if and only if the user
   * buys a custom domain from us.
   *
   * Eg, nickgrossman.xyz imports from Wordpress, and their Wordpress entry for a given post
   * is determined to be /2021/01/01/my-post/. We store 2021/01/01/my-post here, and redirect
   * to this post if someone visits nickgrossman.xyz/2021/01/01/my-post.
   *
   * This is an array becase we may have multiple original URLs for a given post.
   */
  redirectFrom?: string[]

  /**
   * If the user wants to publish this post at a future date, then this will be set to that date.
   * Unix timestamp in milliseconds.
   * If this is undefined, then the post is published immediately.
   * If this is set, then the post is NOT published immediately, but instead is scheduled for publishing
   * at the specified date.
   * If null is supplied, then we're telling the API to clear the scheduled date.
   */
  scheduledAt?: number | null

  /**
   * If this is true, then the post has been scheduled for publishing at a future date.
   * It is possible for scheduledAt to be set without this being true. This is the interim state
   * during which a user has selected a scheduled date, but not yet hit the "Publish" button.
   * Once the task has been queued up in GCP Tasks, then this will be set to true.
   */
  isScheduled?: boolean

  /**
   * If this is true, then the post is not meant to appear in any public feeds, including the blog's homepage
   * and the blog's RSS feed.
   * This is useful for pinned posts, which appear outside the feed, or for posts that are meant to be
   * sent as newsletters only but for which the author still wants to have a working link.
   * By default this is false.
   */
  isUnlisted: boolean

  /**
   * The language of the post.
   *
   * This is a two-letter ISO 639-1 code, e.g. "en" for English.
   *
   * Can be undefined for older posts that were created before we added language detection.
   * Can also be null if we failed to detect the language with enough confidence or for some other reason.
   */
  language?: string | null

  /**
   *
   * This is the user ID of the user that owns the note.
   *
   * This is identical to the Firestore parent.id. We store it here
   * for redundancy, to make certain queries easier.
   */
  userId: string

  /**
   *
   * This is the blog ID of the blog that owns the note.
   *
   * The hierarchy we have in Firestore is:
   *
   *  1. User > Blog
   *  2. User > Note
   *
   * So, this ID is the blog ID on the same level of the Note (both under the same user ID).
   * We store it here for redundancy, to make certain queries easier.
   */

  blogId: string

  /**
   * An object of which note values have been AI generated.
   */
  aiGenerated?: AiGeneratedPostValues
}

// @deprecated in favor of new token gating system that uses GatingRules.
export type ACCESS_BLOCKED_REASON =
  | "NO_TOKEN"
  | "NO_PAID_SUB"
  | "NO_SUB"
  | undefined

export interface UserNotes {
  notes: Array<Note>
}

export interface Comment {
  id: string
  userId?: string // The user that made the comment
  noteId: string // The note that the comment is on
  text: string
  createdAt: number
  updatedAt?: number

  replyToCommentId?: string // only if the comment is a reply
}

export type CommentWithUser = Comment & {
  user?: BaseUserPublic
  blog?: Blog
}

export type CommentAndRepliesWithUser = {
  comment: CommentWithUser
  replies?: Array<CommentAndRepliesWithUser>
}

/**
 * Combines both paragraph and farcaster comment data in a standardized format.
 */
export type CommentResp = {
  // *****-------------------*****
  // *****   SHARED FIELDS   *****
  // *****-------------------*****

  /**
   * The platform this comment is from.
   */
  platform: "paragraph" | "farcaster"

  /**
   * For a Paragraph comment, this is the internal ID of the comment in Firestore.
   *
   * For a Farcaster cast, this is the hash of the cast.
   */
  id: string

  /**
   * The user ID of the user that made the comment.
   */
  userId?: string

  /**
   * The URL of the avatar of the author of this comment.
   */
  avatarUrl?: string

  /**
   * The name of the author of this comment.
   */
  authorName: string

  /**
   * The text of this comment.
   */
  text: string

  /**
   * The timestamp when this comment was created.
   */
  createdAt: number

  /**
   * Replies to this comment. Applicable to both Paragraph and Farcaster comments.
   */
  replies?: CommentResp[]

  // *****---------------------------*****
  // *****   PARAGRAPH-ONLY FIELDS   *****
  // *****---------------------------*****

  /**
   * Whether the current user is the owner of the comment.
   *
   * This is used by the FE to determine whether to show edit and delete buttons.
   * But we still do authorization checks on the back-end.
   *
   * Not relevant to Farcaster comments.
   */
  paragraphIsOwner?: boolean

  // *****---------------------------*****
  // *****   FARCASTER-ONLY FIELDS   *****
  // *****---------------------------*****

  /**
   * The number of likes this comment has received.
   * Specific to Farcaster comments, as Paragraph comments don't have likes.
   */
  farcasterLikesCount?: number

  /**
   * The username of the author on Farcaster. Specific only to Farcaster casts.
   */
  farcasterUsername?: string

  farcasterEmbeds?: Array<{
    url?: string
  }>

  /**
   * The number of recasts this comment has received.
   * Specific to Farcaster comments, as Paragraph comments don't have recasts.
   */
  farcasterRecastsCount?: number

  /**
   * URL of the cast on warpcast.
   */
  farcasterCastUrl?: string
}

export interface SuccessResponse {
  success: true
}

// *****-----------------*****
// *****   ERROR CODES   *****
// *****-----------------*****

/**
 * Error codes used in the optional `code` field ErrorResponse type.
 * We only use these when we want to correlate a back-end error with
 * some kind of special handling in the front-end.
 */
export type ErrorCode =
  /**
   * This error code is used when a user tries to subscribe while logged out,
   * but the email they are trying to subscribe with already exists in the system
   * so they actually need to log in first.
   */
  | "SUBSCRIBING_USER_LOGGED_OUT_BUT_EMAIL_ALREADY_EXISTS"
  | "INCORRECT_PASSWORD"
  | "ALREADY_SUBSCRIBED"

export type ErrorType = "info" | "warning" | "success" | "error"
export interface ErrorResponse {
  success?: false
  type?: ErrorType
  msg: string
  /**
   * This is a code we can use to uniquely identify the error
   * for special handling in some use cases.
   */
  code?: ErrorCode
}

export function isErrorResponse(arg: any): arg is ErrorResponse {
  return arg && arg.msg && typeof arg.msg == "string"
}

export type SUCCESS_RESPONSE = {
  status: "SUCCESS"
}

export type FAILURE_RESPONSES = {
  status: "ERROR_TRANSIENT" | "ERROR_PERMANENT"
  error: string
}

/**
 * Response from any API endpoint that is returned to a GCP task client,
 * where we can have a success state, or one of two errors, transient or permanent.
 * Transient errors are retried by GCP, permanent errors are not.
 */
export type GCPTaskResponseStatus = SUCCESS_RESPONSE | FAILURE_RESPONSES

export interface GetNoteParams {
  id: string
}

export interface PostNoteResponse {
  note: GetNoteResp
}

export type ExtendedUser = User & { notes: Array<Note> } & { blog: Blog } & {
  subscriptions: Array<Subscription>
}

export interface AdminStats {
  users: Array<ExtendedUser>
  //posts: Array<Note>
  comments: Array<Comment>
}

export interface ImportFileResponse {
  success: true
  jobId: string
}

// Used for verifying the user's custom domain
export interface GetCustomDomainResponse {
  success: boolean
  verified: boolean
  verification?: Array<{
    type: "TXT" | "CNAME" | "A"
    domain: string
    value: string
  }>
}

export type CommentNotification = {
  type: "comment"
  commentId: string
  noteId: string
}

export type ExtendedCommentNotification = CommentNotification & {
  commenterUser: User
  commenterBlog: Blog
  comment: Comment
  note: Note
}

export type SubscriptionNotification = {
  type: "subscription"
  subscriberUserId: string
  //subscriberUserId: string
  subscriptionId: string
}

export type ExtendedSubscriptionNotification = SubscriptionNotification & {
  subscriberUser: User
  subscriberUserBlog: Blog
}

export type BaseNotification = {
  id?: string
  userId: string // The user that needs to be notified.
  //blogId: string
  read?: boolean
  //userId: string
  //type: "comment" | "subscription"
  //comment_id?:
  //text: string
  createdAt: number
  updatedAt?: number
}

export type Notification = BaseNotification &
  (CommentNotification | SubscriptionNotification)

export type NotificationResp = BaseNotification &
  (ExtendedCommentNotification | ExtendedSubscriptionNotification)

/**
 * Using a "composite" cursor because we want to primarily order by publishedAt, but due to the non-uniqueness
 * of that field, we need the `id` to be a tiebreaker.
 */
export type DiscoverCursor = { publishedAt: number; id: string }

export type DiscoverEntry = {
  note: Note
  blog: Blog
}

export type DiscoverResp = { size: number; entries: Array<DiscoverEntry> }

/** Post search data type. published is included if not searching only published posts (T) */
export type PostSearchData<T extends boolean> = T extends true
  ? Pick<Note, "id" | "title">
  : Pick<Note, "id" | "title" | "published">

// Returned when requesting a Note from the API.
// Contains both Note fields stored in Firestore, and non-firestore fields that are added for convenient access.
export type GetNoteResp = Note & {
  // List of one or more users that have contributed to this post.
  // Contains not just their user IDs but also bio details, profile pics, etc.
  // for convenient display when loading a note.
  authorDetails: BaseUserPublic[]
}

/**
 * Contains the minimal fields that are returned when querying for a preview list of notes.
 * This is the list that is loaded in the sidebar of the editor when a use first logs in.
 * It's only when they click on a note that we load the full note.
 */
export type GetNotePreviewResp = Pick<
  Note,
  (typeof GetNotePreviewRespFields)[number]
>

/**
 * This type represents either a preview of a note, which contains just minimal fields to display that note in a sidebar,
 * or a full note, which contains all fields including `json`, `staticHtml`, and additional metadata fields that aren't
 * needed in the preview. It's typed this way to make all the preview fields mandatory and all the other fields that are
 * on the note but not in the preview note, optional.
 */
export type GetNoteRespFullOrPreview = GetNotePreviewResp &
  Partial<Omit<GetNoteResp, keyof GetNotePreviewResp>>

export interface PublicNotesResp {
  notes: Array<GetNoteResp>
  size: number
  /**
   * Posts the author has pinned to the top of their home page.
   * These don't get paginated and are always returned in full.
   *
   * These do not get populated by the API when the reader is querying
   * for a single, specific post by slug.
   */
  pinnedPosts?: Array<GetNoteResp>
}

// Deprecated in favor of the new "Token" type below, used in conjunction with one or more GatingRules.
// export type Membership = {
//   name: string

//   // If this is empty or undefined, the membership
//   // is open to everyone
//   accessRestriction: {
//     // Deprecated.
//     // token?: ImportedToken | ParagraphMintedToken
//     email_subscribers?: boolean
//     stripe_subscribers?: boolean
//   }
// }

// Deprecated in favor of the new "Token" type below, used in conjunction with one or more GatingRules.
// This is what's stored in Firestore.
export type MembershipEntry = (Membership | MembershipWithNFT) & {
  id: string
  blogId: string
}

// *****-----------------*****
// *****   COMMUNITIES   *****
// *****-----------------*****

export type Community = {
  name: string
  /**
   * Description of what this community is about
   */
  description: string

  membershipIds?: Array<string>

  // @DEPRECATED: Remove this before pushing
  /*accessRestriction: {
    token?: TokenAccessRestriction
    everyone?: boolean
    email_subscribers?: boolean
    stripe_subscribers?: boolean
  }*/
}

// This is what's stored in Firestore. Maps the community to the blog.
export type CommunityEntry = Community & {
  id: string
  blogId: string
}

/**
 * Returned by the API when querying for community data. Contains both Firestore fields and enriched API data
 * that comes from additional queries, such as community member count.
 */
export type CommunityEntryRes = CommunityEntry & {
  // Count of members in the community - enriched by the API.
  memberCount: number
}

export type SupportedNetworks =
  | "ethereum"
  | "matic"
  | "bsc"
  | "gnosis"
  | "avalanche"
  | "arbitrum"
  | "optimism"
  | "base"
  | "custom"

export type ContractMetadataReturn = {
  name: string
  network: SupportedNetworks
}

export type ContractMetadataResp =
  | { success: false }
  | ({
      success: true
    } & ContractMetadataReturn)

// Farcaster entry, returned from the Farcaster API.
export type CastEntry = {
  body: {
    publishedAt: number
    username: string
    data: {
      text: string
      image: null | string
      replyParentMerkleRoot: null | string
      threadMerkleRoot: string | null
    }
  }
  meta: {
    displayName: string
    avatar: string
    numReplyChildren: number
    reactions: {
      count: number
      type: "Like"
    }
    recasts: {
      count: number
    }

    watches: {
      count: number
    }
    replyParentUsername: {
      username: null
    }
    mentions: []
  }
  merkleRoot: string
  uri: string
}

export type CastEntryCache = BaseCache & { entries: Array<CastEntry> }

type BaseCache = {
  createdAt: number
  expiresAt: number
}

// Response from the Farcaster API.
export type CastsResponse = {
  casts: Array<CastEntry>
}

// *****----------------*****
// *****   CRYPTO API   *****
// *****----------------*****

// Response returned by the /crypto/balances API when retrieving a set of balances for a user for an array of contract addresses.
export type BalanceResp = {
  contractAddress: string
  onChainId?: string // ERC1155 token ID.
  balance: string
}

// *****--------------*****
// *****   DB CACHE   *****
// *****--------------*****

// The docId is the key to the cache.
export type DbCache = {
  // JSON string stored in the cache.
  value: string

  createdAt: number
  expiresAt: number
}

export default this

// *****---------------*****
// *****  MEMBERSHIPS  *****
// *****---------------*****

export type PlanType =
  | "free"
  | "stripe-subscription"
  | "stripe-payment"
  | "loop-subscription"
  | "comp"

export type PlanPeriod = "day" | "week" | "month" | "year"

export type Plan = {
  /** These will be defined if Stripe is enabled */
  stripeProductId?: string
  /**
   * This is the exact same thing as the Subscription planId.
   */
  stripePriceId?: string
  /** This will be defined if Loop is enabled */
  loopItemId?: string
  price: number
  /** Defined for reoccurring prices */
  period?: PlanPeriod
  /**
   * Reoccuring period is a multiple of how many of the period unit
   * Example: periodFrequency: 2, period: week => every 2 weeks
   * NOTE: This is currently unused
   */
  periodFrequency?: number
}

export type MembershipStatus = "ACTIVE" | "ARCHIVED"

export type SubscriptionUnsubscribeType = "MANUAL" | "PAYMENT_FAILED"

export type MembershipSubscriberCountResp = {
  total: number
  active: number
  inactive: number
}

export type Membership = {
  id: string
  blogId?: string
  status?: MembershipStatus
  name: string
  description: string
  benefits?: string[]
  /** Can be paid or free (plan with id: 'free' and price: 0) */
  plans: Plan[]
  /**
   * Whether this plan is recommended by the author or not. Shows up as "Most popular" in the UI.
   * Only one plan can be recommended at a time.
   */
  isRecommended: boolean
  image?: ImageWithPlaceholder

  /**
   * If hasNFT is true, this means this membership has an NFT associated to it,
   * but does NOT mean the NFT exists on-chain yet. The NFT is only minted when
   * a user first mints it (eg lazy minting).
   */
  hasNFT: boolean
  /**
   * This is only present if hasNFT is true.
   */
  chain?: CHAINS
}

/** Tokens are lazy minted so this will get populated after it is created on first reader mint */
export type MembershipWithNFT = Membership & {
  hasNFT: true
  /**
   * This is the LOWERCASE contract address.
   */
  contractAddress: `0x${string}`
  chain: CHAINS
}

export interface Subscription {
  id: string
  /**
   * The blog that the user is subscribed to.
   */
  blogId: string
  /**
   * The user ID of the subscribing user.
   */
  userId: string
  /**
   * @deprecated This is only used for legacy purposes. Use userId going forward.
   */
  email?: string
  paid?: boolean
  createdAt: number
  updatedAt?: number

  /**
   * If the user clicked subscribe based upon a blog's recommendation,
   * this is the recommending blog's collectibleWalletAddress or the owning user's lowercase_wallet_address
   */
  referrer?: string

  /** the ID of the subscription on Stripe */
  subscription_id?: string
  /** the ID of the subscription item on Stripe (needed for switching plans) */
  subscription_item_id?: string
  /** the subscriber's customer ID for the blog's Stripe Connect account */
  stripe_connect_customer_id?: string
  /** the Loop agreement (subscription) ID */
  loop_agreement_id?: string
  /** the subscriber's Loop entity ID */
  loop_entity_id?: string
  membershipId: string
  /**
   * This is the same thing as the Stripe Price ID or Loop Entity ID. We can query
   * on Membership.plans[0].stripePriceId or Membership.plans[0].loopItemId to join a subscription with a membership.
   */
  planId: string
  planType: PlanType

  /** The ID of the coupon the subscriber used when subscribing */
  couponId?: string
  /** For a trial, the end timestamp */
  trialEndAt?: number

  /** Whether this was a manual subscription (ie, one the blog owner added themself), an imported subscription (such as from Substack), or an organic one (ie, someone subscribed AND verified organically, from the user's blog). */
  type?: SubscriptionType

  /** Whether the subscription is unsubscribed or active. */
  status?: SubscriptionStatus

  /**
   * Optional. The list of community IDs within the blog, or non-community emails
   * that the user has opted out of subscribing to.
   * If this is undefined or empty, the user is subscribed to all the communities
   * and non-community emails.
   *
   * This array should be mutually exclusive with `optIns`.
   * We're doing it this way to aide in Firestore queries.
   */
  optOuts?: Array<string>

  /**
   * Optional. The list of community IDs within the blog, or non-community emails
   * that the user has opted into subscribing to.
   * If this is undefined or empty, the user is not subscribed to any of the communities
   * and non-community emails.
   *
   * This array should be mutually exclusive with `optOuts`.
   * We're doing it this way to aide in Firestore queries.
   */
  optIns?: Array<string>

  /** The date that the subscription will cancel due to manual cancellation. */
  toBeUnsubscribedAt?: number
  /** The date that the user unsubscribed. */
  unsubscribedAt?: number
  /** The reason for the unsubscribed. */
  unsubscribeType?: SubscriptionUnsubscribeType

  /**
   * When a creator enables NFTs for their membership, and a user mints one,
   * we update this field with the specific tokenId that they hold.
   * The actual contract address of the NFT is set on the membership itself.
   *
   * Note that the token ID is of type @bigint (not string) because it's a 256-bit number,
   * but we store it as string here.
   */
  tokenId?: string

  /**
   * Serialized string that could contain zero or more UTM params.
   * e.g. {"utm_medium":"paid","utm_campaign":"W3A Conv. Cold -> Leads"}
   */
  utm?: string

  /**
   * Whether we want to deliver notifications on Farcaster.
   */
  farcasterNotifications?: boolean
}

export type ImportType = "free" | "paid"
export type SubstackPaidSubType = "paid" | "comp"

export type StripeImportMappingOption = {
  name: string
  membershipId?: string
  id: string
}

export type StripeImportMapping = {
  from?: Partial<StripeImportMappingOption>
  to?: Partial<StripeImportMappingOption>
}

export type StripeImportResult = {
  status: "success" | "error"
  error?: string
  numImported?: number
  numSkipped?: number
  numErrors?: number
}

/** This is the dicount object used to display the discount for a membership on the membership card */
export type MembershipDiscount = {
  /** ID of the discount -- this is what is used in the URL and, for coupons, is also used as the ID for the coupon in Stripe */
  discountId: string
  /** ID of the plan this discount obect is for */
  planId: string
  /** Percentage off original price */
  percentOff?: number
  /** Plan price with the discount applied */
  discountedPrice?: number
  /** How many trial days there will be with the discount applied */
  trialDays?: number
  /** Description of the coupon to be shown on checkout page */
  description?: string
  /** The DATETIME that the coupon can be redeemed until */
  expiresAt?: string
  /** Toggle to show expiration countdown on membership page */
  showExpirationCountdown?: boolean
}

/** This is the discount object that is stored in Firestore */
export type Discount = {
  /** Status of the discount */
  status: "active" | "inactive"
  /** Type of discount (trial --- x number of days free, coupon --- creates Stripe coupon to give percent off) */
  type: "trial" | "coupon"
  id: string
  /** Blog the discount is for */
  blogId: string
  /** Name of the discount */
  name: string
  /** Description of the coupon to be shown on checkout page */
  description?: string
  /** Percent off for discount (if coupon) */
  percentOff?: number
  /** Number of trial days for discount (if trial) */
  trialDays?: number
  /** Number of discount redemptions (uses) */
  redemptions: number
  /** The UTC time that the coupon can be redeemed until */
  expiresAt?: number
  /** Toggle to show expiration countdown on membership page */
  showExpirationCountdown?: boolean
  createdAt: number
}

export type LoopItemFrequency = "HOUR" | "DAY" | "WEEK" | "MONTH" | "YEAR"
export type LoopItem = {
  /** The item ID in LoopCrypto */
  itemId: string
  /** The name of the item */
  name: string
  /** If the item is active */
  active: boolean
  /** If should auto invoice */
  autoInvoice: boolean
  /** Membership ID in Firestore */
  externalId: string
  /** ID of item type: https://docs.loopcrypto.xyz/technical-docs/architecture/items/adding-items/item-types */
  typeId: number
  /** The price in USD of the item */
  amount: number
  /** The charging frequency for the item */
  frequency: LoopItemFrequency
  /** Count modifier for frequency (e.g. every 2 weeks) */
  frequencyCount: number
  acceptedTokens: Partial<LoopAcceptedTokens>
}

export type LoopNetworkName =
  | "Ethereum"
  | "Optimism"
  | "Polygon"
  | "Arbitrum"
  | "Binance"
  | "Base"
  | "Goerli"

export type LoopTokenName =
  | "USDC"
  | "GUSD"
  | "WBTC"
  | "WETH"
  | "DAI"
  | "BUSD"
  | "FRAX"
  | "ENS"
  | "DYDX"
  | "UNI"
  | "USDT"
  | "WMATIC"
  | "WBNB"
  | "USDCbc"
  | "cbETH"

export type LoopNetwork = {
  network: LoopNetworkName
  id: number
  tokens: { name: LoopTokenName; address: string }[]
}

export type LoopTokenSetting = {
  token: string
}

/** inboundTreasuries format for entity API */
export type InboundTreasuries = {
  /** string array of wallet addresses */
  [networkId: string]: string[]
}

/** acceptedTokens format for items API */
export type LoopAcceptedTokens = {
  [networkId in LoopNetworkName]: string[]
}

/** Loop Entity (account) */
export type LoopEntity = {
  /** Entity name (i.e. blog name) */
  name: string
  /** The wallet to accept payments */
  inboundTreasuries: { [networkId: string]: string[] }
  /** Blog logo */
  logoUrl: string
  /** Blog URL */
  websiteUrl: string
  /** Loop items for the membership plans */
  items: Optional<LoopItem, "itemId">[]
  /** Should charge platform fee */
  networkFeeEnabled: boolean
  /** Blog email (used for Loop emails and login) */
  email: string
  /** Publication email (used for the replyTo email in Loop emails to subscribers) */
  replyTo: string
}

export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>

export type LoopWebhookAgreementSignedUp = {
  event: string
  networkId: number
  contractAddress: string
  email: string
  company: string
  parent: string
  subscriber: string
  item: string
  subscriptionAmount: number
  agreementId: string
  agreementAmount: string
  frequencyNumber: number
  frequencyUnit: LoopItemFrequency
  paymentTokenSymbol: LoopTokenName
  eventDate: number
  refId: string
  metadata: { couponCode?: string; trialDays?: number; discount?: number }
}

/**
 * Docs at: https://docs.loopcrypto.xyz/technical-docs/architecture/webhooks#transferprocessed
 */
export type LoopWebhookTransferProcessed = {
  event: string
  transaction: string
  networkId: number
  contractAddress: string
  email: string
  company: string
  parent: string
  paymentId: string
  success: boolean
  token: string
  paymentTokenSymbol: LoopTokenName
  endUser: string
  reason: string
  agreementId: string
  frequencyUnit: LoopItemFrequency
  /**
   * The amount paid in the token's smallest unit (e.g. 1e18 for ETH).
   */
  amountPaid: number
  refId: string
  /**
   * The amount paid in USD.
   */
  usdAmount: number
}

export type LoopWebhookAgreementCancelled = {
  event: string
  transaction: string
  networkId: number
  contractAddress: string
  email: string
  company: string
  parent: string
  entityId: string
  subscriber: string
  agreementId: string
  agreementStartDate: number
  date: number
  eventDate: number
  itemId: string
  itemName: string
  refId: string
}

export type LoopWebhookLatePayment = {
  event: string
  transaction: string
  networkId: number
  networkName: string
  contractAddress: string
  email: string
  company: string
  subscriber: string
  dueDate: number
  amountDue: number
  lateReason: "Insufficient balance" | "Insufficient allowance"
  agreementId: string
  eventDate: number
}

export type LoopMetadataEntry = {
  state: "SUBSCRIBING" | "VERIFIED" | "CANCELLING"
  userId: string
  blogId: string
  noteId?: string
  agreementId?: string
  membershipId: string
  itemId: string
  entityId: string
  wallet: string
  couponId: string | null
  createdAt: number
}

export type RecommendationItem = {
  id: string
  reason?: string
}

export type RecommendationModalItem = {
  url: string
  reason?: string
}

export type BlogRecommendation = {
  /** ID of the recommended blog. */
  id: string
  /** actual URL to the recommended blog. */
  url: string
  /** lowercase_url (@...) of the recommended blog. This is required for inline subscribing */
  lowercase_url: string
  /** logo URL of the recommended blog */
  logo_url?: string
  /** name of the recommended blog */
  name: string
  /** short summary of the recommended blog */
  summary?: string
  /** reason for recommending blog */
  reason?: string
}

/**
 * A blog introduction is an optional "Hero" section at the top of a blog which can be used
 * by the author to provide a brief introduction to the blog, highlight recent posts, or provide
 * other information to readers.
 *
 * You can think of this as a "special" note in the sense that it's always published, there's
 * no concept of drafts, and there's no title or subtitle. It's just a block of text (and optionally
 * embeds) that are displayed at the top of the blog.
 *
 * This is its own root-level collection.
 *
 * We're not storing this within a `blog` document because of concerns around maxing out document
 * size limits in Firestore. We're storing this in a separate collection and querying it separately.
 *
 * We're also not storing this as a `note` because it's not a note; it's a special section of the blog
 * that's always displayed at the top. The fields are far too different, there's no concept of drafts,
 * and the noteHandlers logic for dealing with notes is already far too complex to accommodate this
 * special case.
 */
export type BlogIntroduction = {
  /**
   * ID of the blog introduction. This is just the document ID.
   */
  id: string

  /**
   * The blog ID that this introduction is associated with.
   */
  blogId: string

  /** The introduction text in tiptap JSON format. This is in the same format as a note. */
  json: string

  /**
   * Contains pre-rendered HTML that is ready to display to a reader.
   */
  staticHtml: string
}

export type POST_FILTER =
  | "all"
  | "published"
  | "scheduled"
  | "unpublished"
  | "archived"

export type PostSubscribeState =
  | "EMAIL_ENTRY"
  | "WALLET_ENTRY"
  | "SET_PASSWORD"
  | "LOGIN"
